import { ArrowDownIcon, ArrowUpIcon, ChevronLeftIcon, ChevronRightIcon, ChevronsUpDownIcon, DownloadIcon, SearchIcon, SearchXIcon, XIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ErrorState } from '@/components/ErrorState';
import { TableSkeleton } from '@/components/LoadingBlock';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { downloadCsv, toCsv } from '@/lib/csv';
import { fmtNumber } from '@/lib/format';
import { compare, DEFAULT_PAGE_SIZES, matchesQuery, useTableState, type SortDir, type SortValue } from '@/lib/table';
import { useKeyed, useQueryState } from '@/lib/url';
import { cn } from '@/lib/utils';

export interface Column<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Makes the column sortable. */
  sortValue?: (row: T) => SortValue;
  /** Text searched by the toolbar query. */
  searchValue?: (row: T) => string | null | undefined;
  /** Value written to CSV exports (falls back to sortValue, then searchValue). */
  csv?: (row: T) => string | number | boolean | null | undefined;
  /** Exclude from CSV (action columns). */
  noExport?: boolean;
  align?: 'left' | 'right';
  /** Class names for both the header and the cells (widths, responsive hiding). */
  className?: string;
  headerClassName?: string;
  cellClassName?: string;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterDef<T> {
  key: string;
  label: string;
  options: FilterOption[];
  predicate: (row: T, value: string) => boolean;
  /** Segmented buttons (default for <= 4 options) or a dropdown. */
  kind?: 'segmented' | 'select';
  /** Show per-option counts (faceted against the other active filters). */
  counts?: boolean;
}

export interface BulkContext<T> {
  selected: T[];
  /** Deselect everything. */
  clear: () => void;
  /** Every row matching the current search/filters (across pages). */
  filteredCount: number;
  allFilteredSelected: boolean;
  selectAllFiltered: () => void;
}

/** Paging done by the API: the table shows the rows it was given and its pager walks the server's pages. */
export interface ServerPaging {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

export interface DataTableProps<T> {
  data: T[] | undefined;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  columns: Array<Column<T>>;
  rowId: (row: T) => string;
  filters?: Array<FilterDef<T>>;
  searchPlaceholder?: string;
  defaultSort?: { id: string; dir: SortDir };
  /** Namespaces the URL keys when a page has more than one table. */
  stateKey?: string;
  onRowClick?: (row: T) => void;
  rowHref?: (row: T) => string;
  rowClassName?: (row: T) => string | undefined;
  /** Shown when the source has no rows at all (before any filtering). */
  emptyState?: ReactNode;
  /** Extra controls rendered at the right end of the toolbar. */
  toolbar?: ReactNode;
  /** Hide the toolbar entirely (tiny tables). */
  noToolbar?: boolean;
  /** Rows per page to start with; people can change it from the pager, which keeps the choice in the URL. */
  pageSize?: number;
  pageSizes?: number[];
  /** Hand paging to the API: `data` is one page and the pager reports the server's totals. Search and filters then apply to that page only. */
  server?: ServerPaging;
  /** Bind `/` to focus the search box (leave on for the page's primary table only). */
  hotkey?: boolean;
  /** Row id to highlight, e.g. from `?row=`. */
  highlightId?: string | null;
  dense?: boolean;
  className?: string;
  /** Adds a checkbox column. Selected rows are handed to `bulkActions`. Shift-click selects a range. */
  selectable?: boolean;
  /** Rows that cannot be selected (e.g. yourself). */
  selectDisabled?: (row: T) => boolean;
  bulkActions?: (ctx: BulkContext<T>) => ReactNode;
  /** File name (without .csv) that enables the Export button. Exports the filtered rows, or the selection when there is one. */
  exportName?: string;
  /** On phones, each row renders as this card instead of a table row (no selection there). */
  mobileCard?: (row: T) => ReactNode;
}

/**
 * The app's list table: URL-synced search, faceted filters, sortable headers, pagination, optional
 * multi-select (click, Shift-click for a range, select-all across pages) with a bulk action bar, and
 * CSV export. Mirrors the operations console's table so both apps behave the same.
 */
export function DataTable<T>({
  data,
  loading,
  error,
  onRetry,
  columns,
  rowId,
  filters = [],
  searchPlaceholder = 'Search…',
  defaultSort,
  stateKey,
  onRowClick,
  rowHref,
  rowClassName,
  emptyState,
  toolbar,
  noToolbar,
  pageSize = 25,
  pageSizes = DEFAULT_PAGE_SIZES,
  server,
  hotkey = true,
  highlightId,
  dense,
  className,
  selectable,
  selectDisabled,
  bulkActions,
  exportName,
  mobileCard,
}: DataTableProps<T>) {
  const filterKeys = useMemo(() => filters.map((f) => f.key), [filters]);
  const state = useTableState(filterKeys, { prefix: stateKey, defaultSort, defaultPageSize: pageSize, pageSizes });
  const k = useKeyed(stateKey);
  const [sp] = useQueryState();
  const highlighted = highlightId ?? sp.get(k('row'));
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const anchorRef = useRef<string | null>(null);
  const shiftRef = useRef(false);

  // `/` focuses the search box when not already typing.
  useEffect(() => {
    if (!hotkey || noToolbar) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hotkey, noToolbar]);

  const searchable = useMemo(() => columns.filter((c) => c.searchValue), [columns]);
  const rows = data ?? [];

  const applyAll = (skip?: string): T[] => {
    let out = rows;
    for (const f of filters) {
      if (f.key === skip) continue;
      const v = state.filters[f.key];
      if (v) out = out.filter((r) => f.predicate(r, v));
    }
    if (state.q && searchable.length) out = out.filter((r) => matchesQuery(searchable.map((c) => c.searchValue?.(r) ?? '').join(' '), state.q));
    return out;
  };

  const filtered = useMemo(() => {
    let out = applyAll();
    if (state.sort) {
      const col = columns.find((c) => c.id === state.sort);
      if (col?.sortValue) {
        const sv = col.sortValue;
        out = [...out].sort((a, b) => compare(sv(a), sv(b), state.dir));
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filters, state.filters, state.q, state.sort, state.dir, columns]);

  // Client paging over the filtered rows, or the server's numbers when the page fetches one page at a time.
  const size = server ? server.pageSize : state.pageSize;
  const total = server ? server.total : filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const page = Math.min(server ? server.page : state.page, pageCount);
  const start = (page - 1) * size;
  const slice = server ? filtered : filtered.slice(start, start + size);
  const setPage = server ? server.onPageChange : state.setPage;
  const setSize = server ? server.onPageSizeChange : state.setPageSize;
  const clickable = Boolean(onRowClick || rowHref);

  // Selection is kept by id and pruned against the current rows, so a refetch never selects ghosts.
  const selectedRows = useMemo(() => (selectedIds.size ? rows.filter((r) => selectedIds.has(rowId(r))) : []), [rows, selectedIds, rowId]);
  const selectableSlice = slice.filter((r) => !selectDisabled?.(r));
  const pageAllSelected = selectableSlice.length > 0 && selectableSlice.every((r) => selectedIds.has(rowId(r)));
  const pageSomeSelected = selectableSlice.some((r) => selectedIds.has(rowId(r)));
  const filteredSelectable = filtered.filter((r) => !selectDisabled?.(r));
  const allFilteredSelected = filteredSelectable.length > 0 && filteredSelectable.every((r) => selectedIds.has(rowId(r)));
  const clearSelection = () => setSelectedIds(new Set());
  const togglePage = () => {
    anchorRef.current = null;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) for (const r of selectableSlice) next.delete(rowId(r));
      else for (const r of selectableSlice) next.add(rowId(r));
      return next;
    });
  };
  /** Tick or untick one row; with Shift held, every selectable row between the last clicked row and this one. */
  const toggleOne = (id: string, shift: boolean) => {
    const ids = filteredSelectable.map(rowId);
    const i = ids.indexOf(id);
    const a = anchorRef.current ? ids.indexOf(anchorRef.current) : -1;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const on = !prev.has(id);
      if (shift && a >= 0 && i >= 0) for (const x of ids.slice(Math.min(a, i), Math.max(a, i) + 1)) on ? next.add(x) : next.delete(x);
      else on ? next.add(id) : next.delete(id);
      return next;
    });
    anchorRef.current = id;
  };

  const exportCsv = () => {
    const source = selectedRows.length ? selectedRows : filtered;
    const cols = columns.filter((c) => !c.noExport && (typeof c.header === 'string' ? c.header.trim().length > 0 : true));
    const header = cols.map((c) => (typeof c.header === 'string' ? c.header : c.id));
    const body = source.map((r) =>
      cols.map((c) => {
        const v = c.csv ? c.csv(r) : c.sortValue ? c.sortValue(r) : (c.searchValue?.(r) ?? '');
        return v instanceof Date ? v.toISOString() : (v as string | number | boolean | null | undefined);
      }),
    );
    downloadCsv(`${exportName ?? 'export'}-${new Date().toISOString().slice(0, 10)}`, toCsv(header, body));
  };

  if (error) return <ErrorState error={error} onRetry={onRetry} />;

  const showToolbar = !noToolbar && (rows.length > 0 || loading || state.active);
  const colCount = columns.length + (selectable ? 1 : 0);
  // The pager shows once a rows-per-page choice matters (or when a link points past the first page).
  const rangeEnd = Math.min(start + (server ? rows.length : slice.length), total);
  const pager =
    total > Math.min(...pageSizes) || page > 1 ? (
      <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t px-3 py-2 text-xs">
        <span className="tabular-nums">
          {total === 0 ? 'No rows' : `${fmtNumber(start + 1)}–${fmtNumber(rangeEnd)} of ${fmtNumber(total)}`}
          {server && state.active ? ` · ${fmtNumber(filtered.length)} on this page match` : ''}
        </span>
        <div className="flex items-center gap-2">
          {setSize && (
            <Select value={String(size)} onValueChange={(v) => setSize(Number(v))}>
              <SelectTrigger size="sm" className="w-[104px] text-xs" aria-label="Rows per page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizes.map((s) => (
                  <SelectItem key={s} value={String(s)}>
                    {s} / page
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon-sm" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page">
              <ChevronLeftIcon />
            </Button>
            <span className="px-1 tabular-nums">
              {page} / {pageCount}
            </span>
            <Button variant="ghost" size="icon-sm" disabled={page >= pageCount} onClick={() => setPage(page + 1)} aria-label="Next page">
              <ChevronRightIcon />
            </Button>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {showToolbar ? (
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            {searchable.length > 0 ? (
              <div className="relative w-full sm:max-w-xs">
                <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
                <Input ref={searchRef} value={state.q} onChange={(e) => state.setQuery(e.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder} className="bg-card pr-12 pl-8" />
                <div className="pointer-events-none absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1">
                  {state.q ? (
                    <button type="button" className="text-muted-foreground hover:text-foreground pointer-events-auto grid size-5 place-items-center" onClick={() => state.setQuery('')} aria-label="Clear search">
                      <XIcon className="size-3.5" />
                    </button>
                  ) : hotkey ? (
                    <kbd className="bg-muted text-muted-foreground inline-flex h-5 min-w-5 items-center justify-center border px-1 font-mono text-[10px] font-medium">/</kbd>
                  ) : null}
                </div>
              </div>
            ) : null}
            {filters.map((f) => {
              const value = state.filters[f.key] ?? '';
              const base = f.counts ? applyAll(f.key) : null;
              const count = (v: string) => (base ? base.filter((r) => f.predicate(r, v)).length : undefined);
              const kind = f.kind ?? (f.options.length <= 4 ? 'segmented' : 'select');
              if (kind === 'segmented') {
                return (
                  <div key={f.key} role="group" aria-label={f.label} className="bg-card inline-flex h-9 items-stretch border text-xs">
                    <button type="button" onClick={() => state.setFilter(f.key, null)} aria-pressed={!value} className={cn('flex items-center gap-1.5 border-r px-2.5 font-medium transition-colors', !value ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}>
                      All{base ? <span className="tabular-nums opacity-70">{base.length}</span> : null}
                    </button>
                    {f.options.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => state.setFilter(f.key, value === o.value ? null : o.value)}
                        aria-pressed={value === o.value}
                        className={cn('flex items-center gap-1.5 border-r px-2.5 font-medium transition-colors last:border-r-0', value === o.value ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}
                      >
                        {o.label}
                        {base ? <span className="tabular-nums opacity-70">{count(o.value)}</span> : null}
                      </button>
                    ))}
                  </div>
                );
              }
              return (
                <Select key={f.key} value={value || '__all__'} onValueChange={(v) => state.setFilter(f.key, v === '__all__' ? null : v)}>
                  <SelectTrigger className="bg-card h-9 min-w-36" aria-label={f.label}>
                    <span className="text-muted-foreground">{f.label}:</span>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All</SelectItem>
                    {f.options.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                        {base ? <span className="text-muted-foreground ml-2 tabular-nums">{count(o.value)}</span> : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            })}
            {state.active ? (
              <Button variant="ghost" size="sm" onClick={state.clear} className="text-muted-foreground">
                <XIcon /> Clear
              </Button>
            ) : null}
          </div>
          <div className="flex items-center justify-between gap-3 lg:justify-end">
            <span className="text-muted-foreground text-xs tabular-nums">
              {loading ? '' : filtered.length === rows.length ? `${fmtNumber(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}` : `${fmtNumber(filtered.length)} of ${fmtNumber(rows.length)}`}
            </span>
            {exportName && rows.length > 0 ? (
              <Button variant="ghost" size="sm" onClick={exportCsv} className="text-muted-foreground" title={selectedRows.length ? `Export the ${selectedRows.length} selected rows` : 'Export the rows currently shown'}>
                <DownloadIcon /> Export
              </Button>
            ) : null}
            {toolbar}
          </div>
        </div>
      ) : toolbar ? (
        <div className="flex justify-end">{toolbar}</div>
      ) : null}

      {selectable && selectedRows.length > 0 ? (
        <div role="region" aria-label="Bulk actions" className="bg-muted/50 border-l-primary flex flex-wrap items-center gap-x-3 gap-y-2 border border-l-2 px-3 py-2 text-sm">
          <span className="font-medium tabular-nums">{fmtNumber(selectedRows.length)} selected</span>
          {!allFilteredSelected && filteredSelectable.length > selectedRows.length ? (
            <button type="button" className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline" onClick={() => setSelectedIds(new Set(filteredSelectable.map(rowId)))}>
              Select all {fmtNumber(filteredSelectable.length)}
            </button>
          ) : null}
          <span className="bg-border hidden h-4 w-px sm:block" aria-hidden />
          <div className="flex flex-wrap items-center gap-2">{bulkActions?.({ selected: selectedRows, clear: clearSelection, filteredCount: filteredSelectable.length, allFilteredSelected, selectAllFiltered: () => setSelectedIds(new Set(filteredSelectable.map(rowId))) })}</div>
          <Button variant="ghost" size="sm" className="text-muted-foreground ml-auto" onClick={clearSelection}>
            <XIcon /> Clear
          </Button>
        </div>
      ) : null}

      {loading ? (
        <TableSkeleton rows={5} cols={Math.min(columns.length, 6)} />
      ) : rows.length === 0 ? (
        (emptyState ?? <p className="text-muted-foreground border border-dashed px-4 py-8 text-center text-sm">Nothing here yet.</p>)
      ) : filtered.length === 0 ? (
        <div className="text-muted-foreground flex flex-col items-center gap-3 border border-dashed px-6 py-12 text-center">
          <SearchXIcon className="size-5" aria-hidden />
          <p className="text-sm">No rows match {state.q ? <>“{state.q}”</> : 'these filters'}.</p>
          <Button variant="outline" size="sm" onClick={state.clear}>
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          {mobileCard ? (
            <div className="bg-card divide-y border md:hidden">
              {slice.map((row) => {
                const id = rowId(row);
                return (
                  <div
                    key={id}
                    data-row-id={id}
                    onClick={() => {
                      if (onRowClick) onRowClick(row);
                      else if (rowHref) navigate(rowHref(row));
                    }}
                    className={cn('p-3.5', clickable && 'active:bg-accent/40 cursor-pointer', highlighted === id && 'bg-accent/50', rowClassName?.(row))}
                  >
                    {mobileCard(row)}
                  </div>
                );
              })}
              {pager}
            </div>
          ) : null}
          <div className={cn('bg-card overflow-x-auto border', mobileCard && 'hidden md:block')}>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {selectable ? (
                  <TableHead className="w-10 pr-0">
                    <Checkbox checked={pageAllSelected ? true : pageSomeSelected ? 'indeterminate' : false} onCheckedChange={togglePage} aria-label="Select all rows on this page" disabled={selectableSlice.length === 0} />
                  </TableHead>
                ) : null}
                {columns.map((c) => {
                  const sortable = Boolean(c.sortValue);
                  const active = state.sort === c.id;
                  return (
                    <TableHead key={c.id} aria-sort={active ? (state.dir === 'asc' ? 'ascending' : 'descending') : undefined} className={cn(c.align === 'right' && 'text-right', c.className, c.headerClassName, sortable && 'p-0')}>
                      {sortable ? (
                        <button type="button" onClick={() => state.toggleSort(c.id)} className={cn('group/sort hover:text-foreground flex h-10 w-full items-center gap-1 px-3 text-left transition-colors', c.align === 'right' && 'flex-row-reverse text-right', active && 'text-foreground')}>
                          {c.header}
                          <span className={cn('text-muted-foreground/60 transition-opacity', !active && 'opacity-0 group-hover/sort:opacity-100')}>
                            {active ? state.dir === 'asc' ? <ArrowUpIcon className="size-3" /> : <ArrowDownIcon className="size-3" /> : <ChevronsUpDownIcon className="size-3" />}
                          </span>
                        </button>
                      ) : (
                        c.header
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {slice.map((row) => {
                const id = rowId(row);
                const isHighlighted = highlighted === id;
                const isSelected = selectedIds.has(id);
                const disabled = selectDisabled?.(row) ?? false;
                return (
                  <TableRow
                    key={id}
                    data-row-id={id}
                    data-state={isHighlighted || isSelected ? 'selected' : undefined}
                    aria-selected={selectable ? isSelected : undefined}
                    onClick={() => {
                      if (onRowClick) onRowClick(row);
                      else if (rowHref) navigate(rowHref(row));
                    }}
                    className={cn(clickable && 'cursor-pointer', isHighlighted && 'bg-accent/50 hover:bg-accent/50', rowClassName?.(row))}
                  >
                    {selectable ? (
                      <TableCell className={cn('w-10 pr-0', dense ? 'py-1.5' : 'py-2.5')} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onPointerDown={(e) => {
                            shiftRef.current = e.shiftKey;
                            if (e.shiftKey) e.preventDefault();
                          }}
                          onClick={(e) => {
                            shiftRef.current = e.shiftKey;
                          }}
                          onCheckedChange={() => toggleOne(id, shiftRef.current)}
                          disabled={disabled}
                          aria-label="Select row"
                          title="Shift-click to select a range"
                        />
                      </TableCell>
                    ) : null}
                    {columns.map((c) => (
                      <TableCell key={c.id} className={cn(dense ? 'py-1.5' : 'py-2.5', c.align === 'right' && 'text-right tabular-nums', c.className, c.cellClassName)}>
                        {c.cell(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {pager}
          </div>
        </>
      )}
      {selectable && colCount > 0 ? null : null}
    </div>
  );
}
